import React, { useMemo } from "react";
import { T, AWARD_COLORS, fmt } from "./theme";
import { Card, Progress, Avatar, AwardBadge, AwardTrophy, Btn, SectionHeader, PilulaAwardTime } from "./ui";
import { Icon } from "./Icons";
import { AWARDS } from "./data";
import { AnimalBadge } from "./AnimalIcons";
import {
  calcPontosCorretor, calcAwardVitalicio,
  calcPorTime, calcPorRegional, calcNacional,
  rankingMensalNacional,
} from "./awards";

// ═══════════════════════════════════════════════════════════════════════
// DASHBOARD DO ADMIN / PROPRIETÁRIO
// Visão executiva: KPIs → rede → ranking → metas → status
// ═══════════════════════════════════════════════════════════════════════

export default function DashboardAdmin({ currentUser, corretores, metas, aceleradores, premios, times, metasIndividuais, onNavigate, dataSource }) {

  // ─── Agregações ────────────────────────────────────────────────────
  const totalCorretores = corretores.length;
  const corretoresAtivos = corretores.filter(c => c.ativo !== false);
  const totalAtivos = corretoresAtivos.length;

  const volumeTotal = useMemo(() => corretores.reduce((s, c) => s + (c.vendasValor || 0), 0), [corretores]);
  const volumeAno = useMemo(() => corretores.reduce((s, c) => s + (c.vendasValorAno || 0), 0), [corretores]);
  const volumeMes = useMemo(() => corretores.reduce((s, c) => s + (c.vendasValorMes || 0), 0), [corretores]);
  const volumeSemana = useMemo(() => corretores.reduce((s, c) => s + (c.vendasValorSemana || 0), 0), [corretores]);
  const totalVendas = useMemo(() => corretores.reduce((s, c) => s + (c.vendasDiretas || 0), 0), [corretores]);
  const totalCadastrados = useMemo(() => corretores.reduce((s, c) => s + (c.cadastrados || 0), 0), [corretores]);
  const totalAtivados = useMemo(() => corretores.reduce((s, c) => s + (c.ativados || 0), 0), 0);

  // Awards distribution
  const awardDist = useMemo(() => {
    const dist = {};
    AWARDS.forEach(a => { dist[a.id] = 0; });
    corretores
      .filter(c => c.tipo !== "proprietario" && c.tipo !== "admin")
      .forEach(c => {
        const aw = calcAwardVitalicio(c);
        dist[aw.id] = (dist[aw.id] || 0) + 1;
      });
    return dist;
  }, [corretores]);

  // Rankings
  const topMes = useMemo(() => rankingMensalNacional(corretores).slice(0, 5), [corretores]);
  const timesAgg = useMemo(() => calcPorTime(corretores, times), [corretores, times]);
  const regionais = useMemo(() => calcPorRegional(corretores), [corretores]);
  const nacional = useMemo(() => calcNacional(corretores, "mes"), [corretores]);

  // Estados ativos
  const estados = useMemo(() => {
    const ufs = new Set(corretores.map(c => c.uf).filter(Boolean));
    return [...ufs].sort();
  }, [corretores]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ═══ HEADER ═══ */}
      <div style={{
        background: T.gradient.cardHero,
        border: `1px solid ${T.borderMid}`,
        borderRadius: T.r.xl, padding: "24px 20px",
        boxShadow: T.shadow.lg,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="serif" style={{ fontSize: 22, fontWeight: 600, color: T.gold, letterSpacing: -0.3 }}>
              👋 Olá, {currentUser.nome.split(" ")[0]}
            </div>
            <div style={{ fontSize: 12, color: T.textSec, marginTop: 4 }}>
              Painel executivo · {currentUser.role === "proprietario" ? "Proprietário" : "Administrador"}
            </div>
          </div>
          <div style={{
            padding: "5px 12px", borderRadius: 100,
            background: dataSource === "supabase" ? T.okSoft : T.warnSoft,
            border: `1px solid ${dataSource === "supabase" ? T.ok : T.warn}55`,
            fontSize: 10, fontWeight: 600, color: dataSource === "supabase" ? T.ok : T.warn,
            display: "flex", alignItems: "center", gap: 5,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: dataSource === "supabase" ? T.ok : T.warn,
            }} />
            {dataSource === "supabase" ? "Dados ao vivo · Supabase" : "Dados locais"}
          </div>
        </div>
      </div>

      {/* ═══ KPIs ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }} className="grid-kpis">
        <KpiCard icon="👥" label="Corretoras" valor={totalAtivos} sub={`de ${totalCorretores} cadastradas`} cor={T.brand} />
        <KpiCard icon="🏆" label="Vendas totais" valor={totalVendas} sub="vendas diretas" cor={T.gold} />
        <KpiCard icon="💰" label="Volume total" valor={fmt.brl(volumeTotal)} cor={T.ok} isText />
        <KpiCard icon="📅" label="Volume do mês" valor={fmt.brl(volumeMes)} sub={`semana: ${fmt.brl(volumeSemana)}`} cor={T.info} isText />
        <KpiCard icon="🌱" label="Rede" valor={totalCadastrados} sub={`${totalAtivados} ativados`} cor={T.brand} />
        <KpiCard icon="📍" label="Estados" valor={estados.length} sub={estados.join(", ")} cor={T.platinum} />
      </div>

      {/* ═══ DISTRIBUIÇÃO DE AWARDS ═══ */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
            🎖️ Distribuição de Awards
          </div>
          <Btn variant="ghost" onClick={() => onNavigate?.("ranking")} style={{ fontSize: 11 }}>
            Ver ranking <Icon.arrow size={10} />
          </Btn>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {AWARDS.map(a => {
            const count = awardDist[a.id] || 0;
            const ac = AWARD_COLORS[a.id] || {};
            return (
              <div key={a.id} style={{
                flex: "1 1 80px", minWidth: 80,
                background: ac.bg || T.cardSoft,
                border: `1px solid ${ac.border || T.border}`,
                borderRadius: T.r.md, padding: "12px 10px",
                textAlign: "center",
              }}>
                <div style={{ marginBottom: 4, lineHeight: 1, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 44 }}>
                  {a.id === "cafe" ? (
                    <span style={{ fontSize: 26 }}>☕</span>
                  ) : (
                    <AwardTrophy awardId={a.id} size={40} />
                  )}
                </div>
                <div className="serif" style={{ fontSize: 20, fontWeight: 700, color: ac.fg || T.text }}>
                  {count}
                </div>
                <div style={{ fontSize: 9, color: T.textSub, marginTop: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {a.nome.replace(" Award", "")}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* ═══ TOP 5 DO MÊS ═══ */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
            🏅 Top 5 Nacional do Mês
          </div>
          <Btn variant="ghost" onClick={() => onNavigate?.("ranking")} style={{ fontSize: 11 }}>
            Ranking completo <Icon.arrow size={10} />
          </Btn>
        </div>
        {topMes.length === 0 ? (
          <div style={{ textAlign: "center", padding: 20, color: T.textDim, fontSize: 13 }}>
            Nenhuma pontuação registrada ainda neste mês.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {topMes.map((c, i) => {
              const medals = ["🥇", "🥈", "🥉", "4️⃣", "5️⃣"];
              const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
              const aw = calcAwardVitalicio(c);
              const timeDoc = c.timeId && times ? times.find(t => t.id === c.timeId) : null;
              return (
                <div key={c.id} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 12px",
                  background: i === 0 ? `${T.gold}12` : "transparent",
                  border: i === 0 ? `1px solid ${T.gold}33` : "1px solid transparent",
                  borderRadius: T.r.md,
                }}>
                  <span style={{ fontSize: 18, minWidth: 28, textAlign: "center" }}>{medals[i]}</span>
                  <Avatar initials={initials} size={32} photo={c.foto} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{c.nome}</span>
                      <PilulaAwardTime awardId={aw.id} timeEmoji={timeDoc?.emoji} size="xs" />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 3 }}>
                      <span style={{ fontSize: 10, color: T.textSub }}>📍 {c.cidade}-{c.uf}</span>
                      <span style={{ fontSize: 10, color: T.gold, fontWeight: 600 }}>{c.pontosMes || 0} pts/mês</span>
                      <span style={{ fontSize: 10, color: T.textDim }}>{fmt.brl(c.vendasValorMes || 0)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ═══ TIMES ═══ */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
            ⚡ Times
          </div>
          <Btn variant="ghost" onClick={() => onNavigate?.("config")} style={{ fontSize: 11 }}>
            Gerenciar <Icon.arrow size={10} />
          </Btn>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          {timesAgg.map(t => (
            <div key={t.time.id} style={{
              background: T.cardSoft, border: `1px solid ${T.border}`,
              borderRadius: T.r.md, padding: "14px 16px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: "#1a140f",
                  border: `2px solid #d7be7c44`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20, lineHeight: 1, flexShrink: 0,
                }}>{t.time.emoji || "🏆"}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{t.time.nome}</div>
                  <div style={{ fontSize: 10, color: T.textSub }}>
                    {t.time.cidade}-{t.time.uf} · {t.membros.length} {t.membros.length === 1 ? "corretora" : "corretoras"}
                  </div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <MiniStat label="Volume/sem" valor={fmt.brl(t.valor)} />
                <MiniStat label="Ativações" valor={t.ativados} />
              </div>
              {t.valido && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 9, color: T.textDim, marginBottom: 4 }}>Progresso meta semanal</div>
                  <Progress value={t.valor} max={metas.time.metaVolume} color={T.brand} height={3} />
                </div>
              )}
              {!t.valido && (
                <div style={{ marginTop: 8, fontSize: 9, color: T.warn, fontStyle: "italic" }}>
                  ⚠️ Menos de 3 membros — meta pausada
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* ═══ REGIONAIS ═══ */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
            🗺️ Regionais
          </div>
          <Btn variant="ghost" onClick={() => onNavigate?.("regional")} style={{ fontSize: 11 }}>
            Ver detalhes <Icon.arrow size={10} />
          </Btn>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          {regionais.map(r => (
            <div key={r.uf} style={{
              background: T.cardSoft, border: `1px solid ${T.border}`,
              borderRadius: T.r.md, padding: "12px 14px",
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>
                📍 {r.uf}
              </div>
              <div style={{ fontSize: 10, color: T.textSub, marginBottom: 8 }}>
                {r.cidades.join(", ")} · {r.membros.length} corretoras
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <MiniStat label="Volume/mês" valor={fmt.brl(r.valor)} />
                <MiniStat label="Ativações" valor={r.ativados} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ═══ NACIONAL ═══ */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
          🌎 Nacional · Mês
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}>
          <MiniStat label="Volume" valor={fmt.brl(nacional.valor)} large />
          <MiniStat label="Ativações" valor={nacional.ativados} large />
          <MiniStat label="Estados" valor={nacional.estadosAtivos} large />
          <MiniStat label="Meta Volume" valor={fmt.brl(metas.nacional.metaVolume)} large dim />
          <MiniStat label="Meta Ativações" valor={metas.nacional.metaAtivacoes} large dim />
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 9, color: T.textDim, marginBottom: 4 }}>Progresso volume nacional</div>
          <Progress value={nacional.valor} max={metas.nacional.metaVolume} color={T.gold} height={5} />
        </div>
      </Card>

      {/* ═══ ATALHOS ADMIN ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
        {[
          { id: "vendas", icon: "🛒", label: "Vendas" },
          { id: "corretores", icon: "👥", label: "Corretores" },
          { id: "clientes", icon: "📋", label: "Clientes" },
          { id: "config", icon: "⚙️", label: "Configurações" },
        ].filter(o => currentUser.modules.includes(o.id))
         .map(o => (
          <button
            key={o.id}
            onClick={() => onNavigate?.(o.id)}
            style={{
              background: T.card, border: `1px solid ${T.border}`,
              borderRadius: T.r.md, padding: "16px 12px",
              cursor: "pointer", color: T.text, fontFamily: "inherit",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
              transition: `all 160ms ${T.ease}`,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = T.cardHi; e.currentTarget.style.borderColor = T.brand + "66"; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.card; e.currentTarget.style.borderColor = T.border; }}
          >
            <span style={{ fontSize: 24 }}>{o.icon}</span>
            <span style={{ fontSize: 11, fontWeight: 500 }}>{o.label}</span>
          </button>
        ))}
      </div>

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SUB-COMPONENTES
// ═══════════════════════════════════════════════════════════════════════

function KpiCard({ icon, label, valor, sub, cor, isText }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`,
      borderRadius: T.r.lg, padding: "16px 14px",
      borderLeft: `3px solid ${cor}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontSize: 10, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600 }}>{label}</span>
      </div>
      <div className="serif" style={{ fontSize: isText ? 18 : 28, fontWeight: 700, color: T.text, lineHeight: 1 }}>
        {valor}
      </div>
      {sub && (
        <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

function MiniStat({ label, valor, large, dim }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 2 }}>
        {label}
      </div>
      <div className="serif" style={{
        fontSize: large ? 18 : 14, fontWeight: 600,
        color: dim ? T.textSub : T.text, lineHeight: 1,
      }}>
        {valor}
      </div>
    </div>
  );
}
